import React, { useEffect, useRef, useState } from "react";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import { cn } from "../../lib/utils";

const SLIDER_RADIUS = 16;

const BrightnessBar = ({
  value,
  onChange,
  onValueUpdate,
  disabled = false,
  min = 0,
  max = 100,
  sliderRef,
  trackColor,
  textColor = "#FFFFFF",
  opacity = 1,
  showLevel = false,
  showText = "",
  showWarningIcon = false,
  onToggle = null,
}) => {
  const barRef = sliderRef || useRef(null);
  const dragging = useRef(false);
  const [showPercentage, setShowPercentage] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const newValueRef = useRef(value);
  const touchStartRef = useRef(null);
  const isScrolling = useRef(false);
  const hasMoved = useRef(false);
  const [isTapping, setIsTapping] = useState(false);
  const mouseStartRef = useRef(null);
  const lastTouchTimeRef = useRef(0);

  // Use localValue during dragging for immediate visual feedback
  const displayValue = dragging.current ? localValue : value;
  const percent = Math.max(0, Math.min(1, (displayValue - min) / (max - min)));

  // Sync localValue with prop value when not dragging
  useEffect(() => {
    if (!dragging.current) {
      setLocalValue(value);
    }
  }, [value]);

  const handleMove = (clientX, clientY) => {
    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    let x = clientX - rect.left;
    x = Math.max(0, Math.min(rect.width, x));
    const newPercent = x / rect.width;
    const newValue = Math.round(newPercent * (max - min) + min);
    setLocalValue(newValue); // Update local state immediately for visual feedback
    if (onChange) onChange({ stopPropagation: () => {} }, newValue);
    newValueRef.current = newValue;
  };

  const handleMouseDown = (e) => {
    if (disabled) return;
    if (Date.now() - lastTouchTimeRef.current < 500) return;
    dragging.current = false;
    hasMoved.current = false;
    mouseStartRef.current = {
      x: e.clientX,
      y: e.clientY,
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!mouseStartRef.current) return;

    if (dragging.current) {
      hasMoved.current = true;
      handleMove(e.clientX, e.clientY);
      return;
    }

    const deltaX = Math.abs(e.clientX - mouseStartRef.current.x);
    const deltaY = Math.abs(e.clientY - mouseStartRef.current.y);
    const moveThreshold = 3;

    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      dragging.current = true;
      hasMoved.current = true;
      setShowPercentage(true);
      handleMove(e.clientX, e.clientY);
    }
  };

  const handleMouseUp = (e) => {
    if (!mouseStartRef.current) return;

    if (dragging.current) {
      dragging.current = false;
      setShowPercentage(false);
      onValueUpdate(newValueRef.current);
    } else if (!hasMoved.current && onToggle) {
      // User clicked without moving - trigger toggle with animation
      setIsTapping(true);
      setTimeout(() => setIsTapping(false), 200);
      onToggle();
    }

    dragging.current = false;
    hasMoved.current = false;
    mouseStartRef.current = null;
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e) => {
    if (disabled) return;
    lastTouchTimeRef.current = Date.now();
    dragging.current = false;
    isScrolling.current = false;
    hasMoved.current = false;
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleTouchEnd);
  };

  const handleTouchMove = (e) => {
    if (!touchStartRef.current || isScrolling.current) return;

    if (dragging.current) {
      hasMoved.current = true;
      handleMove(e.touches[0].clientX, e.touches[0].clientY);
      return;
    }

    const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
    const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
    const moveThreshold = 5;

    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      if (deltaY > deltaX) {
        isScrolling.current = true;
        touchStartRef.current = null;
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
      } else {
        dragging.current = true;
        hasMoved.current = true;
        setShowPercentage(true);
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (isScrolling.current) {
      return;
    }
    if (dragging.current) {
      setShowPercentage(false);
      onValueUpdate(newValueRef.current);
    } else if (!hasMoved.current && onToggle) {
      // User tapped without moving - trigger toggle with animation
      setIsTapping(true);
      setTimeout(() => setIsTapping(false), 200);
      onToggle();
    } else {
      handleMove(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
      onValueUpdate(newValueRef.current);
    }
    dragging.current = false;
    isScrolling.current = false;
    hasMoved.current = false;
    touchStartRef.current = null;
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
  };

  const getTrackWidth = () => {
    if (!barRef.current) return "0%";
    return `${percent * 100}%`;
  };

  return (
    <div
      ref={barRef}
      className={cn(
        "relative w-full h-full overflow-visible select-none transition-all",
        disabled ? "cursor-default opacity-80" : "cursor-pointer"
      )}
      style={{
        background: "hsl(var(--muted))",
        borderRadius: `${SLIDER_RADIUS}px`,
        opacity: disabled ? 0.8 : opacity,
        transform: isTapping ? "scale(0.95)" : "scale(1)",
        transition: "opacity 0.2s ease-in-out, transform 0.2s ease-in-out",
        WebkitTapHighlightColor: "transparent",
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* Text and Level Display inside bar */}
      {(showLevel || showText) && (
        <div
          className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-0.5 z-[3] pointer-events-none"
          style={{
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {showText && (
            <span
              className="font-bold"
              style={{
                color: textColor,
              }}
            >
              {showText}
            </span>
          )}
          {showLevel && (
            <span
              style={{
                color: textColor,
              }}
            >
              {Math.round(displayValue)}%
            </span>
          )}
          {showWarningIcon && (
            <SyncProblemIcon style={{ color: textColor, fontSize: 16 }} />
          )}
        </div>
      )}

      {showPercentage && (
        <div
          className="absolute -top-[25px] -translate-x-1/2 bg-white rounded-lg px-2 py-1 text-black text-sm font-bold z-[4] pointer-events-none shadow-lg"
          style={{
            left: `${percent * 100}%`,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
          }}
        >
          {Math.round(displayValue)}%
        </div>
      )}
      <div
        className="absolute left-0 top-0 h-full z-[1] pointer-events-none"
        style={{
          width: getTrackWidth(),
          background: trackColor || "hsl(var(--warning))",
          borderRadius: `${SLIDER_RADIUS}px`,
          transition: dragging.current ? "none" : "width 0.2s",
        }}
      />
    </div>
  );
};

export default BrightnessBar;
